import { extendTheme } from '@chakra-ui/react';

// Brand Colors
const colors = {
  brand: {
    50: '#e6f3ff',
    100: '#b3d9ff',
    200: '#80bfff',
    300: '#4da6ff',
    400: '#1a8cff',
    500: '#0073e6', // Primary
    600: '#005cb3',
    700: '#004580',
    800: '#002e4d',
    900: '#00171a',
  },
  // Semantic Colors
  success: {
    50: '#e6fff0',
    100: '#b3ffd6',
    200: '#80ffbb',
    300: '#4dffa1',
    400: '#1aff86',
    500: '#00e66c',
    600: '#00b354',
    700: '#00803c',
    800: '#004d24',
    900: '#001a0c',
  },
  warning: {
    50: '#fff9e6',
    100: '#ffecb3',
    200: '#ffdf80',
    300: '#ffd24d',
    400: '#ffc61a',
    500: '#e6ac00',
    600: '#b38600',
    700: '#806000',
    800: '#4d3a00',
    900: '#1a1300',
  },
  // Surface colors for cards/backgrounds
  surface: {
    light: '#ffffff',
    dark: '#1a202c',
  },
  background: {
    light: '#f7fafc',
    dark: '#171923',
  },
};

// Typography
const fonts = {
  heading: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
  body: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
};

const fontSizes = {
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  md: '1rem',       // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem',    // 48px
};

const fontWeights = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// Spacing (8px based)
const space = {
  px: '1px',
  0.5: '0.125rem',  // 2px
  1: '0.25rem',     // 4px
  1.5: '0.375rem',  // 6px
  2: '0.5rem',      // 8px
  2.5: '0.625rem',  // 10px
  3: '0.75rem',     // 12px
  3.5: '0.875rem',  // 14px
  4: '1rem',        // 16px
  5: '1.25rem',     // 20px
  6: '1.5rem',      // 24px
  7: '1.75rem',     // 28px
  8: '2rem',        // 32px
  9: '2.25rem',     // 36px
  10: '2.5rem',     // 40px
  12: '3rem',       // 48px
  14: '3.5rem',     // 56px
  16: '4rem',       // 64px
  20: '5rem',       // 80px
  24: '6rem',       // 96px
};

// Border Radius
const radii = {
  none: '0',
  sm: '0.25rem',    // 4px
  base: '0.375rem', // 6px
  md: '0.5rem',     // 8px
  lg: '0.75rem',    // 12px
  xl: '1rem',       // 16px
  '2xl': '1.5rem',  // 24px
  '3xl': '2rem',    // 32px
  full: '9999px',
};

// Shadows
const shadows = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  base: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  md: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  lg: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  // Colored shadows for cards
  brand: '0 4px 14px 0 rgba(0, 115, 230, 0.39)',
  success: '0 4px 14px 0 rgba(0, 230, 108, 0.39)',
  warning: '0 4px 14px 0 rgba(230, 172, 0, 0.39)',
};

// Transitions
const transition = {
  property: {
    common: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
    colors: 'background-color, border-color, color, fill, stroke',
    dimensions: 'width, height',
    position: 'left, right, top, bottom',
    background: 'background-color, background-image, background-position',
  },
  easing: {
    'ease-in': 'cubic-bezier(0.4, 0, 1, 1)',
    'ease-out': 'cubic-bezier(0, 0, 0.2, 1)',
    'ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
  duration: {
    'ultra-fast': '50ms',
    faster: '100ms',
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '400ms',
    'ultra-slow': '500ms',
  },
};

// Component Styles
const components = {
  Button: {
    baseStyle: {
      fontWeight: 'semibold',
      borderRadius: 'lg',
      transition: 'all 0.2s ease-in-out',
    },
    variants: {
      solid: (props) => ({
        bg: props.colorScheme === 'brand' ? 'brand.500' : undefined,
        color: 'white',
        _hover: {
          bg: props.colorScheme === 'brand' ? 'brand.600' : undefined,
          transform: 'translateY(-1px)',
          boxShadow: 'md',
        },
        _active: {
          transform: 'translateY(0)',
        },
      }),
      ghost: {
        _hover: {
          bg: 'gray.100',
        },
      },
    },
  },
  Card: {
    baseStyle: {
      container: {
        borderRadius: 'xl',
        boxShadow: 'sm',
        transition: 'all 0.2s ease-in-out',
        _hover: {
          boxShadow: 'md',
        },
      },
    },
  },
  Input: {
    defaultProps: {
      focusBorderColor: 'brand.500',
    },
    variants: {
      outline: {
        field: {
          borderRadius: 'lg',
          _focus: {
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
          },
        },
      },
    },
  },
  Select: {
    defaultProps: {
      focusBorderColor: 'brand.500',
    },
    variants: {
      outline: {
        field: {
          borderRadius: 'lg',
        },
      },
    },
  },
  Table: {
    variants: {
      simple: {
        th: {
          fontWeight: 'semibold',
          textTransform: 'none',
          letterSpacing: 'normal',
          fontSize: 'sm',
          color: 'gray.600',
        },
        td: {
          fontSize: 'sm',
        },
      },
    },
  },
  Modal: {
    baseStyle: {
      dialog: {
        borderRadius: '2xl',
      },
    },
  },
  Drawer: {
    baseStyle: {
      dialog: {
        bg: 'white',
        _dark: {
          bg: 'gray.800',
        },
      },
    },
  },
  Badge: {
    baseStyle: {
      borderRadius: 'full',
      fontWeight: 'medium',
      textTransform: 'none',
    },
  },
  Alert: {
    baseStyle: {
      container: {
        borderRadius: 'lg',
      },
    },
  },
  Tooltip: {
    baseStyle: {
      borderRadius: 'md',
      px: 3,
      py: 2,
    },
  },
};

// Global Styles
const styles = {
  global: (props) => ({
    body: {
      bg: props.colorMode === 'dark' ? 'gray.900' : 'gray.50',
      color: props.colorMode === 'dark' ? 'white' : 'gray.800',
    },
    '*::selection': {
      bg: 'brand.100',
      color: 'brand.900',
    },
  }),
};

// Config
const config = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

const theme = extendTheme({
  colors,
  fonts,
  fontSizes,
  fontWeights,
  space,
  radii,
  shadows,
  transition,
  components,
  styles,
  config,
});

export default theme;
